import { useTranslation } from "react-i18next";

import { Loader } from "@mantine/core";

import { ShieldCheckIcon } from "@prostocheck/controls-web";
import { useFormatters } from "@prostocheck/i18n";
import { Button } from "@prostocheck/ui";

import { StepHeading } from "../step-heading";
import { IConfirmStepProps } from "./confirm-step.models";

/**
 * Step two: whose key it is, then one signed request to ДПС. The certificate
 * is shown before signing in because a taxpayer may hold several keys, and
 * only one of them opens the ПРРО they came for.
 */
export const ConfirmStep = ({
  owner,
  isBusy,
  error,
  onBack,
  onSubmit,
}: IConfirmStepProps) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();

  return (
    <>
      <StepHeading
        icon={<ShieldCheckIcon className="size-5.5" />}
        title={t("login.confirm.title")}
        description={t("login.confirm.description")}
      />

      <div className="mt-7 space-y-5">
        {owner && (
          <dl className="divide-y divide-ink-900/8 rounded-xl bg-surface-muted px-4 ring-1 ring-ink-900/5 ring-inset">
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="text-sm text-ink-500">
                {t("login.confirm.owner")}
              </dt>
              <dd className="text-right text-sm font-medium text-ink-900">
                {owner.name}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="text-sm text-ink-500">
                {t("login.confirm.taxNumber")}
              </dt>
              <dd className="text-sm font-medium tabular-nums text-ink-900">
                {owner.taxNumber}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="text-sm text-ink-500">
                {t("login.confirm.issuer")}
              </dt>
              <dd className="text-right text-sm font-medium text-ink-900">
                {owner.issuer}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="text-sm text-ink-500">
                {t("login.confirm.validTo")}
              </dt>
              <dd className="text-sm font-medium tabular-nums text-ink-900">
                {formatDate(owner.validTo)}
              </dd>
            </div>
          </dl>
        )}

        {error}

        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="lg"
            disabled={isBusy}
            onClick={onBack}
          >
            {t("login.confirm.otherKey")}
          </Button>
          <Button
            size="lg"
            className="flex-1"
            disabled={isBusy}
            onClick={onSubmit}
          >
            {isBusy && <Loader size="xs" color="white" />}
            {isBusy ? t("login.confirm.signingIn") : t("login.confirm.submit")}
          </Button>
        </div>
      </div>
    </>
  );
};
